/**
 * O LEITOR DE CÓDIGO DE BARRAS PELA CÂMERA — o mesmo para o Giro e para a venda.
 *
 * Android/Chrome tem o leitor nativo (BarcodeDetector). iPhone/Safari NÃO tem, e alguns Android
 * têm o objeto mas sem EAN (sem Google Play Services): antes a tela dizia "Leitor indisponível
 * neste aparelho" e o iPhone nunca bipava. Onde o nativo não lê EAN, entra o ZXing em WebAssembly
 * (pacote `barcode-detector`, mesma interface). O .wasm sai do NOSSO servidor — nunca de CDN.
 */
import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

/** Os códigos que existem nas caixas (os nomes são os do BarcodeDetector). */
export type Formato = 'ean_13' | 'ean_8' | 'code_128' | 'upc_a' | 'upc_e';

export type Detector = {
  detect: (fonte: HTMLVideoElement | HTMLCanvasElement) => Promise<{ rawValue: string }[]>;
};
type ConstrutorNativo = {
  new (o: { formats: string[] }): Detector;
  getSupportedFormats?: () => Promise<readonly string[]>;
};

/** O nativo só serve se lê EAN-13 — é o código de toda caixa da farmácia. */
export function nativoLeEan(suportados: readonly string[]): boolean {
  return suportados.includes('ean_13');
}

async function nativo(w: { BarcodeDetector?: unknown }): Promise<ConstrutorNativo | null> {
  const Ctor = w.BarcodeDetector as ConstrutorNativo | undefined;
  if (typeof Ctor !== 'function') return null;
  try {
    // Sem getSupportedFormats não dá para saber: confia no nativo (é o caso raro).
    if (!Ctor.getSupportedFormats) return Ctor;
    return nativoLeEan(await Ctor.getSupportedFormats()) ? Ctor : null;
  } catch {
    return null;
  }
}

/** Cria o leitor: o nativo quando ele lê EAN, senão o ZXing. Lança se nenhum dos dois subir. */
export async function criarDetector(
  formatos: readonly Formato[],
  w: { BarcodeDetector?: unknown } = window as unknown as { BarcodeDetector?: unknown },
): Promise<Detector> {
  const N = await nativo(w);
  if (N) return new N({ formats: [...formatos] });
  const { BarcodeDetector, prepareZXingModule } = await import('barcode-detector/ponyfill');
  // Carrega o .wasm agora (e daqui): se falhar, a tela diz na hora em vez de "ler" para sempre.
  await prepareZXingModule({
    overrides: {
      locateFile: (caminho: string, prefixo: string) =>
        caminho.endsWith('.wasm') ? wasmUrl : prefixo + caminho,
    },
    fireImmediately: true,
  });
  return new BarcodeDetector({ formats: [...formatos] });
}

/**
 * O aparelho abre a câmera? Só depende de getUserMedia agora (página sem HTTPS não tem) — o leitor
 * existe em todo navegador. Os parâmetros existem para o teste; a tela chama sem nada.
 */
export function temCamera(
  nav: { mediaDevices?: { getUserMedia?: unknown } } | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator,
): boolean {
  return typeof nav?.mediaDevices?.getUserMedia === 'function';
}

/* ------------------------------------------------------------------ *
 * A CÂMERA QUE LÊ DE PRIMEIRA (dono, 29/09: "desfoca, tem que abrir e já ler")
 *
 * O que desfocava: a pessoa encosta o celular na caixa e a lente não foca tão perto. Aqui:
 *   - Full HD traseira com foco CONTÍNUO (quando o aparelho deixa);
 *   - ZOOM ~2× quando existe: lê a um palmo, onde o foco funciona;
 *   - lê só a FAIXA DO MEIO (a mira), redimensionada — mais rápido e mais certo no ZXing;
 *   - tocar na imagem pede foco de novo; lanterna quando o aparelho tem.
 * ------------------------------------------------------------------ */

type Capacidades = MediaTrackCapabilities & {
  focusMode?: string[];
  zoom?: { min: number; max: number; step?: number };
  torch?: boolean;
};

/** Zoom que deixa ler a um palmo: 2× (ou o máximo, se menor); sem zoom real, nada. */
export function zoomIdeal(z: { min: number; max: number } | undefined): number | null {
  if (!z || !(z.max >= 1.5)) return null;
  return Math.max(z.min, Math.min(2, z.max));
}

/** A faixa do meio do quadro (onde fica a mira): 90% da largura × 45% da altura. */
export function recorteCentral(largura: number, altura: number) {
  const w = Math.round(largura * 0.9);
  const h = Math.round(altura * 0.45);
  return { x: Math.round((largura - w) / 2), y: Math.round((altura - h) / 2), w, h };
}

/** Abre a câmera traseira em alta resolução; aparelho que recusa as preferências abre no básico. */
export async function abrirCameraTraseira(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        // foco contínuo: o Chrome do Android aceita aqui; os outros ignoram
        advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
      },
    });
  } catch (e) {
    // Permissão negada não se resolve pedindo de novo com menos: devolve o erro.
    if (e instanceof DOMException && e.name === 'NotAllowedError') throw e;
    return navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' } },
    });
  }
}

/** Liga foco contínuo e zoom quando o aparelho tem. Devolve se há lanterna. Nunca lança. */
export async function ajustarCamera(fluxo: MediaStream): Promise<{ lanterna: boolean }> {
  const t = fluxo.getVideoTracks()[0];
  const cap = (t?.getCapabilities?.() ?? {}) as Capacidades;
  if (!t) return { lanterna: false };
  const avancado: Record<string, unknown>[] = [];
  if (cap.focusMode?.includes('continuous')) avancado.push({ focusMode: 'continuous' });
  const z = zoomIdeal(cap.zoom);
  if (z !== null) avancado.push({ zoom: z });
  if (avancado.length)
    await t
      .applyConstraints({ advanced: avancado })
      .catch(() => undefined);
  return { lanterna: cap.torch === true };
}

/** Toque na imagem: foco de novo (single-shot) e volta ao contínuo. Nunca lança. */
export async function focarDeNovo(fluxo: MediaStream): Promise<void> {
  const t = fluxo.getVideoTracks()[0];
  const cap = (t?.getCapabilities?.() ?? {}) as Capacidades;
  if (!t || !cap.focusMode) return;
  const modo = cap.focusMode.includes('single-shot') ? 'single-shot' : cap.focusMode[0];
  await t
    .applyConstraints({ advanced: [{ focusMode: modo } as MediaTrackConstraintSet] })
    .catch(() => undefined);
  if (cap.focusMode.includes('continuous'))
    setTimeout(() => {
      void t
        .applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] })
        .catch(() => undefined);
    }, 900);
}

/** Liga/desliga a lanterna. Devolve se conseguiu. */
export async function usarLanterna(fluxo: MediaStream, ligar: boolean): Promise<boolean> {
  const t = fluxo.getVideoTracks()[0];
  if (!t) return false;
  try {
    await t.applyConstraints({ advanced: [{ torch: ligar } as MediaTrackConstraintSet] });
    return true;
  } catch {
    return false;
  }
}

/**
 * Lê um quadro: primeiro a faixa do meio (mais rápida e mais certa); a cada 3 tentativas, o
 * quadro inteiro — para quem não centraliza o código. `n` é o número da tentativa.
 */
export async function lerQuadro(
  detector: Detector,
  video: HTMLVideoElement,
  tela: HTMLCanvasElement,
  n: number,
): Promise<string | null> {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;
  let fonte: HTMLVideoElement | HTMLCanvasElement = video;
  if (n % 3 !== 2) {
    const r = recorteCentral(vw, vh);
    const escala = Math.min(1, 1280 / r.w);
    tela.width = Math.round(r.w * escala);
    tela.height = Math.round(r.h * escala);
    const ctx = tela.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      ctx.drawImage(video, r.x, r.y, r.w, r.h, 0, 0, tela.width, tela.height);
      fonte = tela;
    }
  }
  const achados = await detector.detect(fonte);
  return achados[0]?.rawValue.trim() || null;
}
